import { useContext } from 'react';
import { AuthZeroApolloContext } from '@src/views/components/util/external/AuthZeroApolloClient';
import { ENVIRONMENT_IS_DEVELOPMENT } from '@src/constants';
// Use the API URL from the environment, and fallback to a default value if not set

// 'http://bluesky.corval.localhost:5000/api/BlueSky/';
export interface ChatMessage {
  Role: 'system' | 'user' | 'assistant | divider'; // Indicates the role of the message
  Content: string; // The content of the message
  History: any[];
}
export interface ImportResult {
  CreatedCategories: number;
  CreatedSubCategories: number;
  CreatedSubObjectives: number;
  CreatedActivities: number;
  CreatedStaticProjectActivities: number;
}

// Shape returned by the tng.SideMenuContent stored procedure.
export interface SideMenuConsultant {
  id: string;
  accountId: string;
  fullName: string;
  accessLevel: string | null;
}

export interface SideMenuClient {
  id: string;
  name: string;
}

export interface SideMenuContentResponse {
  consultants: SideMenuConsultant[];
  clients: SideMenuClient[];
}

export interface SmartSheetImportRequest {
  SmartsheetToken: string;
  SheetId: string;
  ProjectId: string;
  ProjectVersionId: string;
  AccountId: string;
  IncludeContainerRows: boolean;
  NewProjectVersionName?: string;
  NewProjectVersionDescription?: string;
}

export interface SmartSheetExportRequest {
  SmartsheetToken: string;
  ProjectVersionId: string;
  AccountId: string;
  ExportMode: 'create' | 'update';
  SheetId?: string;
  NewSheetName?: string;
  IncludeContainerRows: boolean;
}

export interface DocumentStorageUploadResponse {
  success: boolean;
  documentId: string;
  message?: string;
}

export interface DocumentSharepointLinkResponse {
  success: boolean;
  documentVersionId?: string;
  versionNumber?: number;
  message?: string;
}

export interface DocumentStorageUploadOnlyResponse {
  success: boolean;
  documentId: string;
  blobContainer: string;
  blobName: string;
  contentType: string;
  originalFileName: string;
  extension?: string | null;
  sizeBytes: number;
  sha256?: string | null;
  message?: string;
}

export interface PayrollHoursImportResponse {
  success: boolean;
  rowsInserted?: number;
  rowsUpdated?: number;
  rowsUnchanged?: number;
  duplicatesSkipped?: number;
  message?: string;
}

export interface DocumentListItem {
  documentId: string;
  versionNumber: number;
  fileName: string | null;
  originalFileName?: string | null;
  contentType: string;
  sizeBytes: number;
  updatedUtc: string;
}

interface DocumentListFetchCacheEntry {
  data: DocumentListItem[];
  loadedAt: number;
  inFlight?: Promise<DocumentListItem[]>;
}

const documentListFetchCache = new Map<string, DocumentListFetchCacheEntry>();
const DOCUMENT_LIST_FETCH_TTL_MS = 30000;

export const useCentralCalls = () => {
  const { currentToken } = useContext(AuthZeroApolloContext);
  let apiUrl = '';
  if (ENVIRONMENT_IS_DEVELOPMENT === true) {
    apiUrl = `${window.location.protocol}//${window.location.hostname}:5001/api/BlueSky/`;
  } else {
    apiUrl = `${window.location.protocol}//${window.location.hostname}/api/BlueSky/`;
  }
  const getProjectMap = async (versionId: string): Promise<any> => {
    const response = await fetch(`${apiUrl}GetProjectMap`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${currentToken}`,
      },
      body: JSON.stringify({ projectVersionId: versionId }),
    });

    if (!response.ok) {
      throw new Error('Network response was not ok');
    }

    return response.json();
  };

  /** The current user's tng.Consultant row, or null if their account has no consultant record. */
  const getConsultantId = async (): Promise<any | null> => {
    const response = await fetch(`${apiUrl}GetConsultantId`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${currentToken}`,
      },
    });

    if (response.status === 404) {
      return null;
    }
    if (!response.ok) {
      throw new Error('Network response was not ok');
    }

    return response.json();
  };

  const executeStoredProcedureNoParams = async (procedureName: string): Promise<any> => {
    const response = await fetch(`${apiUrl}ExecuteStoredProcedureNoParams`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${currentToken}`,
      },
      body: JSON.stringify({ ProcedureName: procedureName }),
    });

    if (!response.ok) {
      throw new Error('Network response was not ok');
    }

    return response.json();
  };

  const executeStoredProcedure = async (procedureName: string, parameters: object): Promise<any> => {

    const response = await fetch(`${apiUrl}ExecuteStoredProcedure`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${currentToken}`,
      },
      body: JSON.stringify({ ProcedureName: procedureName, Parameters: parameters }),
    });

    if (!response.ok) {
      throw new Error('Network response was not ok');
    }

    return response.json();
  };

  const getDashChartData = async (procedureName: string, chartType: string, parameters: object): Promise<any> => {
    const response = await fetch(`${apiUrl}GetDashChartData`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${currentToken}`,
      },
      body: JSON.stringify({ ProcedureName: procedureName, Parameters: parameters, chartType }),
    });

    if (!response.ok) {
      throw new Error('Network response was not ok');
    }

    return response.json();
  };
  const getDashPivotTableData = async (procedureName: string, pivotColumn: string, pivotDataColumn: string, pivotIdColumn: string, parameters: object): Promise<any> => {
    const response = await fetch(`${apiUrl}GetDashTableData`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${currentToken}`,
      },
      body: JSON.stringify({ ProcedureName: procedureName, PivotColumn: pivotColumn, PivotDataColumn: pivotDataColumn, PivotIdColumn: pivotIdColumn, Parameters: parameters }),
    });

    if (!response.ok) {
      throw new Error('Network response was not ok');
    }

    return response.json();
  };
  const RunInsert = async (tableName: string, parameters: Object): Promise<any> => {
    try {
      const response = await fetch(apiUrl + 'RunInsert', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${currentToken}`
        },
        body: JSON.stringify({
          TableName: tableName,
          Parameters: parameters
        })
      });
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      //const data = await response.json();
      return null;
    } catch (error) {
      console.error('Error executing stored procedure:', error);
      throw error; // Rethrow the error to handle it in the calling function
    }
  }
  const TableSelect = async (tableName: string, parameters: Object): Promise<any> => {
    try {
      console.log('Table select...' + tableName)
      console.log(parameters);
      console.log(`${apiUrl}TableSelect`);

      const response = await fetch(`${apiUrl}TableSelect`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${currentToken}`
        },
        body: JSON.stringify({
          TableName: tableName,
          Parameters: parameters
        })
      });
      if (!response.ok) {
        console.log('error in TableSelect REST Call');
        console.log(response.status);
        throw new Error('Network response was not ok');
      }

      const data = await response.json();
      console.log('donw with restcall..');
      console.log(data);
      return data;
    } catch (error) {
      console.error('Error executing stored procedure:', error);
      throw error; // Rethrow the error to handle it in the calling function
    }
  }
  const RunUpdate = async (tableName: string, id: string, parameters: Object): Promise<any> => {
    try {
      console.log('params..');
      console.log(parameters);
      const response = await fetch(apiUrl + 'RunUpdate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${currentToken}`
        },
        body: JSON.stringify({
          TableName: tableName,
          Parameters: parameters,
          Id: id
        })
      });
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error executing stored procedure:', error);
      throw error; // Rethrow the error to handle it in the calling function
    }
  }
  // Pass `keys` (the full primary key) instead of `id` for tables without an Id column, e.g. composite-key join tables.
  const RunDelete = async (tableName: string, id: string | null, keys?: Record<string, string>): Promise<any> => {
    try {
      const response = await fetch(apiUrl + 'RunDelete', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${currentToken}`
        },
        body: JSON.stringify({
          TableName: tableName,
          Id: id ?? undefined,
          Keys: keys
        })
      });
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      return null;
    } catch (error) {
      console.error('Error running delete:', error);
      throw error; // Rethrow the error to handle it in the calling function
    }
  }
  const GetAIResponse = async (prompt: string, systemContext: string, chatHistory: ChatMessageBasic[]): Promise<any> => {
    try {
      const response = await fetch(apiUrl + 'GetAIResponse', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${currentToken}`,
        },
        body: JSON.stringify({
          Prompt: prompt,
          SystemContext: systemContext,
          History:chatHistory
        }),
      });

      if (!response.ok) {
        throw new Error(`Network response was not ok: ${response.statusText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error calling GetAIResponse:');
      throw error; // Rethrow the error to handle it in the calling function
    }
  };
  const GetAIAssistantResponse = async (prompt: string): Promise<any> => {
    try {
      const response = await fetch(apiUrl + 'CorvalAssistantResponse', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${currentToken}`,
        },
        body: JSON.stringify({
          Prompt: prompt
        }),
      });

      if (!response.ok) {
        throw new Error(`Network response was not ok: ${response.statusText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error calling GetAIResponse:');
      throw error; // Rethrow the error to handle it in the calling function
    }
  };
  const GetAIActivityAgentResponseOld = async (prompt: string): Promise<any> => {
    try {
      const response = await fetch(apiUrl + 'ActivityAgentAssistantResponse', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${currentToken}`,
        },
        body: JSON.stringify({
          Prompt: prompt
        }),
      });

      if (!response.ok) {
        throw new Error(`Network response was not ok: ${response.statusText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error calling GetAIResponse:');
      throw error; // Rethrow the error to handle it in the calling function
    }
  };
  const GetAIActivityAgentResponse = async (prompt: string, threadId?: string): Promise<any> => {
    try {
      const response = await fetch(apiUrl + 'ActivityAgentAssistantResponse', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${currentToken}`,
        },
        body: JSON.stringify({
          Prompt: prompt,
          ThreadId: threadId || null, // explicitly send null if missing
        }),
      });

      if (!response.ok) {
        throw new Error(`Network response was not ok: ${response.statusText}`);
      }

      return await response.json(); // expects: { responses: string[], threadId: string }
    } catch (error) {
      console.error('Error calling GetAIResponse:', error);
      throw error;
    }
  };
  const GetAIProjectAgentResponse = async (prompt: string, threadId?: string): Promise<any> => {
    try {
      const response = await fetch(apiUrl + 'ProjectAgentAssistantResponse', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${currentToken}`,
        },
        body: JSON.stringify({
          Prompt: prompt,
          ThreadId: threadId || null, // explicitly send null if missing
        }),
      });

      if (!response.ok) {
        throw new Error(`Network response was not ok: ${response.statusText}`);
      }

      return await response.json(); // expects: { responses: string[], threadId: string }
    } catch (error) {
      console.error('Error calling GetAIResponse:', error);
      throw error;
    }
  };
  const GetAgenticAIAgentResponse = async (prompt: string, threadId?: string, bryntumGanttJson?: any): Promise<any> => {
    try {
      const response = await fetch(apiUrl + 'AgenticProjectAssistant3', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${currentToken}`,
        },
        body: JSON.stringify({
          Prompt: prompt,
          ThreadId: threadId || null,
          BryntumGanttJson: bryntumGanttJson
        }),
      });

      if (!response.ok) {
        throw new Error(`Network response was not ok: ${response.statusText}`);
      }

      return await response.json(); // expects: { responses: string[], threadId: string }
    } catch (error) {
      console.error('Error calling GetAIResponse:', error);
      throw error;
    }
  };
const GetAIAgentResponse = async (
  agentName: string,
  prompt: string,
  threadId?: string,
  entityIds?: string[]

): Promise<any> => {
  try {
    const response = await fetch(apiUrl + 'AgentAssistantResponse', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${currentToken}`,
      },
      body: JSON.stringify({
        AgentName: agentName,
        Prompt: prompt,
        ThreadId: threadId ?? null,
        EntityIds: entityIds ?? null,
      }),
    });

    if (!response.ok) {
      throw new Error(`Network response was not ok: ${response.statusText}`);
    }

    return await response.json(); // { response: string, threadId: string }
  } catch (error) {
    console.error('Error calling GetAIAgentResponse:', error);
    throw error;
  }
};
  const importSmartsheet = async (request: SmartSheetImportRequest): Promise<any> => {
    const response = await fetch(`${apiUrl}SmartSheetImport`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${currentToken}`,
      },
      body: JSON.stringify(request),
    });

    const responseText = await response.text().catch(() => '');
    if (!response.ok) {
      throw new Error(`Smartsheet import failed (${response.status}): ${responseText}`);
    }

    try {
      return responseText ? JSON.parse(responseText) : null;
    } catch {
      return responseText;
    }
  };

  const importCustomFormatSmartsheet = async (request: SmartSheetImportRequest): Promise<any> => {
    const response = await fetch(`${apiUrl}SmartSheetImportCustomFormat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${currentToken}`,
      },
      body: JSON.stringify(request),
    });

    const responseText = await response.text().catch(() => '');
    if (!response.ok) {
      throw new Error(`Custom Smartsheet import failed (${response.status}): ${responseText}`);
    }

    try {
      return responseText ? JSON.parse(responseText) : null;
    } catch {
      return responseText;
    }
  };

  const exportSmartsheet = async (request: SmartSheetExportRequest): Promise<any> => {
    const response = await fetch(`${apiUrl}SmartSheetExport`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${currentToken}`,
      },
      body: JSON.stringify(request),
    });

    const responseText = await response.text().catch(() => '');
    if (!response.ok) {
      throw new Error(`Smartsheet export failed (${response.status}): ${responseText}`);
    }

    try {
      return responseText ? JSON.parse(responseText) : null;
    } catch {
      return responseText;
    }
  };

  const uploadDocumentToStorage = async (
    clientId: string,
    documentItemId: string,
    file: File
  ): Promise<DocumentStorageUploadResponse> => {
    if (!clientId) {
      throw new Error('clientId is required.');
    }
    if (!documentItemId) {
      throw new Error('documentItemId is required.');
    }
    if (!file) {
      throw new Error('file is required.');
    }

    const form = new FormData();
    form.append('clientId', clientId);
    form.append('documentItemId', documentItemId);
    form.append('file', file);

    const response = await fetch(`${apiUrl}DocumentStorageUpload`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${currentToken}`,
      },
      body: form,
    });

    const responseText = await response.text().catch(() => '');
    let jsonResponse: DocumentStorageUploadResponse | null = null;
    try {
      jsonResponse = responseText ? JSON.parse(responseText) : null;
    } catch {
      jsonResponse = null;
    }

    if (!response.ok) {
      throw new Error(
        jsonResponse?.message
        || `Document upload failed (${response.status}): ${responseText}`
      );
    }

    if (!jsonResponse?.documentId) {
      throw new Error('Document upload succeeded but no documentId was returned.');
    }

    return jsonResponse;
  };

  /** Adds a SharePoint-hosted version (link only, no file) to a document item and makes it active. */
  const addSharepointLinkVersion = async (
    clientId: string,
    documentItemId: string,
    sharepointLink: string
  ): Promise<DocumentSharepointLinkResponse> => {
    if (!clientId) {
      throw new Error('clientId is required.');
    }
    if (!documentItemId) {
      throw new Error('documentItemId is required.');
    }
    if (!sharepointLink?.trim()) {
      throw new Error('A SharePoint link is required.');
    }

    const response = await fetch(`${apiUrl}DocumentSharepointLink`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${currentToken}`,
      },
      body: JSON.stringify({ clientId, documentItemId, sharepointLink: sharepointLink.trim() }),
    });

    const responseText = await response.text().catch(() => '');
    let jsonResponse: DocumentSharepointLinkResponse | null = null;
    try {
      jsonResponse = responseText ? JSON.parse(responseText) : null;
    } catch {
      jsonResponse = null;
    }

    if (!response.ok || !jsonResponse?.success) {
      throw new Error(
        jsonResponse?.message
        || `Saving the SharePoint link failed (${response.status}).`
      );
    }

    return jsonResponse;
  };

  const uploadDocumentToStorageOnly = async (
    clientId: string,
    file: File
  ): Promise<DocumentStorageUploadOnlyResponse> => {
    if (!clientId) {
      throw new Error('clientId is required.');
    }
    if (!file) {
      throw new Error('file is required.');
    }

    const form = new FormData();
    form.append('clientId', clientId);
    form.append('file', file);

    const response = await fetch(`${apiUrl}DocumentStorageUploadOnly`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${currentToken}`,
      },
      body: form,
    });

    const responseText = await response.text().catch(() => '');
    let jsonResponse: DocumentStorageUploadOnlyResponse | null = null;
    try {
      jsonResponse = responseText ? JSON.parse(responseText) : null;
    } catch {
      jsonResponse = null;
    }

    if (!response.ok) {
      throw new Error(
        jsonResponse?.message
        || `Document upload failed (${response.status}): ${responseText}`
      );
    }

    if (!jsonResponse?.documentId) {
      throw new Error('Document upload succeeded but no documentId was returned.');
    }

    return jsonResponse;
  };

  /** Uploads a payroll hours export (.xlsx or .csv); rows are upserted into tng.PayrollHoursExport by RowKey. */
  const uploadPayrollHoursCsv = async (file: File): Promise<PayrollHoursImportResponse> => {
    if (!file) {
      throw new Error('file is required.');
    }

    const form = new FormData();
    form.append('file', file);

    const response = await fetch(`${apiUrl}PayrollHoursImport`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${currentToken}`,
      },
      body: form,
    });

    const responseText = await response.text().catch(() => '');
    let jsonResponse: PayrollHoursImportResponse | null = null;
    try {
      jsonResponse = responseText ? JSON.parse(responseText) : null;
    } catch {
      jsonResponse = null;
    }

    if (!response.ok || !jsonResponse?.success) {
      throw new Error(
        jsonResponse?.message
        || `Hours upload failed (${response.status}).`
      );
    }

    return jsonResponse;
  };

  const downloadDocumentFromStorage = async (clientId: string, documentId: string): Promise<Blob> => {
    if (!clientId) {
      throw new Error('clientId is required.');
    }
    if (!documentId) {
      throw new Error('documentId is required.');
    }

    const response = await fetch(
      `${apiUrl}DocumentStorageDownload/${encodeURIComponent(clientId)}/${encodeURIComponent(documentId)}`,
      {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${currentToken}`,
      },
      }
    );

    if (!response.ok) {
      const responseText = await response.text().catch(() => '');
      throw new Error(`Document download failed (${response.status}): ${responseText}`);
    }

    return response.blob();
  };

  const getDocumentsByItemForClient = async (
    clientId: string,
    documentItemId: string
  ): Promise<DocumentListItem[]> => {
    if (!clientId) {
      throw new Error('clientId is required.');
    }
    if (!documentItemId) {
      throw new Error('documentItemId is required.');
    }

    const cacheKey = `${clientId}::${documentItemId}`;
    const cached = documentListFetchCache.get(cacheKey);

    if (cached && Date.now() - cached.loadedAt < DOCUMENT_LIST_FETCH_TTL_MS) {
      return cached.data;
    }

    if (cached?.inFlight) {
      return cached.inFlight;
    }

    const requestPromise = (async (): Promise<DocumentListItem[]> => {
      const response = await fetch(
        `${apiUrl}DocumentStorageList/${encodeURIComponent(clientId)}/${encodeURIComponent(documentItemId)}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${currentToken}`,
          },
        }
      );

      const responseText = await response.text().catch(() => '');
      let jsonResponse: DocumentListItem[] | null = null;
      try {
        jsonResponse = responseText ? JSON.parse(responseText) : [];
      } catch {
        jsonResponse = null;
      }

      if (!response.ok) {
        throw new Error(`Document list fetch failed (${response.status}): ${responseText}`);
      }

      const list = Array.isArray(jsonResponse) ? jsonResponse : [];
      documentListFetchCache.set(cacheKey, {
        data: list,
        loadedAt: Date.now(),
      });
      return list;
    })();

    documentListFetchCache.set(cacheKey, {
      data: cached?.data ?? [],
      loadedAt: cached?.loadedAt ?? 0,
      inFlight: requestPromise,
    });

    try {
      return await requestPromise;
    } catch (error) {
      documentListFetchCache.delete(cacheKey);
      throw error;
    }
  };

  const importStaticProjectActivities = async (
  file: File,
  projectId: string,
  projectVersionId: string,
  accountId: string,
  projectVersionName?:string,
  projectVersionDescription?:string
): Promise<ImportResult> => {
  if (!file) throw new Error('No file selected.');
  if (!projectVersionId) throw new Error('projectVersionId is required.');
  console.log('RUNNING IMPORT!!');
  console.log(projectId, 'projectversion:', projectVersionId, 'accountid:', accountId)
  console.log('name', projectVersionName, 'desc:', projectVersionDescription);
  const form = new FormData();
  form.append('file', file);
  form.append('projectId', projectId);
  form.append('projectVersionId', projectVersionId);
  form.append('accountId', accountId);
  form.append('newProjectVersionName', projectVersionName);
  form.append('newProjectVersionDescription', projectVersionDescription);
  console.log('FORM DATA:');
  console.log(projectId, projectVersionId, accountId);
  const response = await fetch(`${apiUrl}StaticProjectActivityImport`, {
    method: 'POST',
    headers: {
      // IMPORTANT: do NOT set Content-Type for FormData; browser sets boundary
      'Authorization': `Bearer ${currentToken}`,
    },
    body: form,
  });

  console.log('DONE IJPORT');
  console.log(response);
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`Import failed (${response.status}): ${text}`);
  }

  return response.json();
};
  const downloadStaticProjectActivityTemplate = async (
  format: 'xlsx' | 'csv' = 'xlsx',
  autoDownload: boolean = true
): Promise<Blob> => {
  const url = `${apiUrl}StaticProjectActivityImportTemplate?format=${encodeURIComponent(format)}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${currentToken}`,
      // NOTE: don't set Content-Type for GET blob downloads
    }
  });

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`Template download failed (${response.status}): ${text}`);
  }

  const blob = await response.blob();

  if (autoDownload) {
    const a = document.createElement('a');
    const href = URL.createObjectURL(blob);
    a.href = href;
    a.download = `StaticProjectActivity_Import_Template.${format}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(href);
  }

  return blob;
};
  const AskAiAgentWithLookup = async (
    agentName: string,
    prompt: string,
    lookupEntities?: { objectType: string; objectId: string }[],
    threadId?: string
  ): Promise<any> => {
    try {
      const url = `${apiUrl}AskAiAgentWithLookup`;
      const requestBody = {
        AgentName: agentName,
        Prompt: prompt,
        ThreadId: threadId ?? null,
        LookupEntities: (lookupEntities ?? []).map(e => ({
          ObjectType: e.objectType,
          ObjectId: e.objectId,
        })),
      };
      console.log('[AskAiAgentWithLookup] URL:', url);
      console.log('[AskAiAgentWithLookup] Method: POST');
      console.log('[AskAiAgentWithLookup] Body:', JSON.stringify(requestBody, null, 2));

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${currentToken}`,
        },
        body: JSON.stringify(requestBody),
      });

      console.log('[AskAiAgentWithLookup] Response status:', response.status, response.statusText);
      const responseText = await response.text();
      let jsonResponse: any = null;
      try {
        jsonResponse = responseText ? JSON.parse(responseText) : null;
      } catch {
        jsonResponse = null;
      }

      if (jsonResponse != null) {
        console.log('[AskAiAgentWithLookup] Response body:', JSON.stringify(jsonResponse, null, 2));
      } else {
        console.log('[AskAiAgentWithLookup] Response body (text):', responseText);
      }

      if (!response.ok) {
        console.error('[AskAiAgentWithLookup] Error response body:', responseText);
        throw new Error(`Network response was not ok: ${response.status} ${response.statusText}. ${responseText}`);
      }

      return jsonResponse;
    } catch (error) {
      console.error('[AskAiAgentWithLookup] Error:', error);
      throw error;
    }
  };
  const FullAISearchResponse = async (
    agentName: string,
    prompt: string,
    lookupEntities?: { objectType: string; objectId: string }[],
    threadId?: string
  ): Promise<any> => {
    try {
      const url = `${apiUrl}FullAISearchResponse`;
      const requestBody = {
        AgentName: agentName,
        Prompt: prompt,
        ThreadId: threadId ?? null,
        LookupEntities: (lookupEntities ?? []).map(e => ({
          ObjectType: e.objectType,
          ObjectId: e.objectId,
        })),
      };

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${currentToken}`,
        },
        body: JSON.stringify(requestBody),
      });

      const responseText = await response.text();
      let jsonResponse: any = null;
      try {
        jsonResponse = responseText ? JSON.parse(responseText) : null;
      } catch {
        jsonResponse = null;
      }

      if (!response.ok) {
        const err: any = new Error(
          jsonResponse?.message ||
          `Network response was not ok: ${response.status} ${response.statusText}. ${responseText}`
        );
        err.status = response.status;
        err.statusText = response.statusText;
        err.apiError = jsonResponse;
        err.responseText = responseText;
        throw err;
      }

      if (jsonResponse == null) {
        throw new Error(`FullAISearchResponse returned non-JSON response: ${responseText}`);
      }

      return jsonResponse;
    } catch (error) {
      console.error('[FullAISearchResponse] Error:', error);
      throw error;
    }
  };

  const GetAIConversations = async (): Promise<any[]> => {
    try {
      const url = `${apiUrl}AIConversations`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${currentToken}`,
        },
      });

      const responseText = await response.text();
      let jsonResponse: any = null;
      try {
        jsonResponse = responseText ? JSON.parse(responseText) : null;
      } catch {
        jsonResponse = null;
      }

      if (!response.ok) {
        throw new Error(`Network response was not ok: ${response.status} ${response.statusText}. ${responseText}`);
      }

      return Array.isArray(jsonResponse) ? jsonResponse : [];
    } catch (error) {
      console.error('[GetAIConversations] Error:', error);
      throw error;
    }
  };

  return {
    getProjectMap,
    executeStoredProcedureNoParams,
    executeStoredProcedure,
    getConsultantId,
    getDashChartData,
    TableSelect,
    RunInsert,
    getDashPivotTableData,
    RunUpdate,
    RunDelete,
    GetAIResponse,
    GetAIAgentResponse,
    GetAIActivityAgentResponse,
    GetAIProjectAgentResponse,
    GetAgenticAIAgentResponse,
    importStaticProjectActivities,
    importSmartsheet,
    importCustomFormatSmartsheet,
    exportSmartsheet,
    uploadDocumentToStorage,
    uploadDocumentToStorageOnly,
    addSharepointLinkVersion,
    uploadPayrollHoursCsv,
    downloadDocumentFromStorage,
    getDocumentsByItemForClient,
    downloadStaticProjectActivityTemplate,
    AskAiAgentWithLookup,
    FullAISearchResponse,
    GetAIConversations
  };
};